import _ from 'lodash';
import moment from 'moment';

import React, {
    useContext
} from 'react';

import PropTypes from 'prop-types';

import {
    useIsExportingPPT,
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import {
    useTranslation
} from '../../../../i18n.js';

import LandscapeContext from '../../../contexts/landscapeContext.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';
import Panel from '../../../components/Panel.jsx';
import Label from '../../../components/Label.jsx';
import RIQMetricLineChart from '../../../components/RIQMetricLineChart.jsx';

import {
    RIQPopover
} from '../../../components/RIQPopover.jsx';

import {
    DeltaRelativeInsight,
    NumericalValue,
    Summary
} from '../../../components/Summary.jsx';

import {
    createIncompleteAnnotationRenderer,
    Bar
} from '../../../components/charts/Bar.jsx';

import {
    useComputedCurationConfigValue,
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

function getNumericMetricData(request, result) {

    return {
        raw: _.last(_.last(result.data)),
        value: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded))),
        formatted: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated)))
    };
}

function FollowersSummary({requests, results}) {

    let insight = null;
    const deltaRel = getNumericMetricData(requests.audienceDeltaRel, results.audienceDeltaRel);
    if (deltaRel.raw !== null) {
        insight = (
            <DeltaRelativeInsight
                formatted={deltaRel.formatted}
                value={deltaRel.raw}
            />
        );
    } else {
        insight = (
            <div className={`tlw-mt-1.5`}>
                {`--`}
            </div>
        );
    }

    return (
        <Summary
            description='The number of followers on TikTok.'
            title='Followers'
            insight={insight}
            value={<NumericalValue {...getNumericMetricData(requests.audience, results.audience)} />}
        />
    );
}

function NetFollowersSummary({requests, results}) {

    const numFollowers = getNumericMetricData(requests.netFollowers, results.netFollowers);

    let className = null;
    if (numFollowers.value > 0) {
        className = 'tlw-text-green-400';
    } else if (numFollowers.value < 0) {
        className = 'tlw-text-red-400';
    }

    const props = {
        className,
        ...numFollowers
    };

    return (
        <Summary
            description='The net number of followers gained on TikTok.'
            title='Followers Net Change'
            value={<NumericalValue {...props} />}
        />
    );
}

function FollowerBarTooltip({payload}) {

    if (!payload || !payload.length) {
        return;
    }

    const placement = null;
    const datum = _.last(_.first(payload).payload);

    return (
        <RIQPopover
            triggerNode={<div/>}
            className='tlw-flex tlw-w-full tlw-items-center tlw-gap-[10px] tlw-text-md tlw-font-normal'
            placement={placement || 'top'}
            portalNode={document.body}
            additionalHookProps={{
                visible: true
            }}
        >
            <div>
                <div className='tlw-m-0 tlw-whitespace-nowrap tlw-rounded-t-md tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-md tlw-text-gray-700'>
                    {datum.title}
                </div>
                <div className='tlw-py-4.5'>
                    <div className='tlw-border-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-left even_tlw-border-l'>
                        <Label>Follower Net Change</Label>
                        <div className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                            {datum.value}
                        </div>
                    </div>
                </div>
            </div>
        </RIQPopover>
    );
}

function FollowerLineTooltip({lineConfig, chartConfigs, datum, placement}) {

    if (!datum) {
        return;
    }

    return (
        <RIQPopover
            triggerNode={<div/>}
            className='tlw-flex tlw-w-full tlw-items-center tlw-gap-[10px] tlw-text-md tlw-font-normal'
            placement={placement || 'top'}
            portalNode={document.body}
            additionalHookProps={{
                visible: true
            }}
        >
            <div>
                <div className='tlw-m-0 tlw-whitespace-nowrap tlw-rounded-t-md tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-md tlw-text-gray-700'>
                    {datum.tooltipTitle || datum.xValue}
                </div>

                <div className='tlw-py-4.5'>

                    {_.map(chartConfigs, (chartConfig, idx) => {
                        return (
                            <div
                                key={idx}
                                className='tlw-border-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-left even_tlw-border-l'
                            >
                                <Label>{chartConfig.chartName}</Label>
                                <div
                                    className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                    {chartConfig.metrics[0].expandedFormatter(datum[chartConfig.metrics[0].dataKey])}
                                </div>
                            </div>
                        );
                    })}
                </div>

            </div>
        </RIQPopover>
    );
}

function FollowersByInterval({interval, instanceId, requests, results}) {

    const dataKey = 'audienceByInterval';
    const dataByInterval = RIQMetricLineChart.collateMetricResults(interval, requests, results, [dataKey], null, 1);

    const chartConfigs = [{
        chartName: `Followers Per ${interval}`,
        metrics: [{
            dataKey,
            lineColor: '#6a788c',
            abbreviatedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(requests[dataKey], results[dataKey], RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
            expandedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(requests[dataKey], results[dataKey], RivalIQ.metrics.new.enums.MetricFormatTypes.expanded),
            metricRequest: requests[dataKey]
        }],
        yaxisProps: {
            allowDecimals: false
        }
    }];

    chartConfigs[0].tooltipContent = <FollowerLineTooltip
        chartConfigs={chartConfigs}
    />;

    return (
        <RIQMetricLineChart
            key='line-chart'
            chartKey={instanceId}
            dataByInterval={dataByInterval}
            chartConfigs={chartConfigs}
        />
    );
}

function FollowerChangeByInterval({interval, requests, results}) {

    const timezone = requests.netFollowersByInterval.filters.timePeriod.timezone;
    const intervalMetadata = results.netFollowersByInterval?.calculationMetadata?.domains?.[interval];
    const data = _.map(results.netFollowersByInterval.data, ([companyId, day, value], idx) => {
        const startDate = moment.tz(day, timezone);

        let title = startDate.format('MMM D, YYYY');
        if (interval !== 'day') {
            const endDate = moment.tz(startDate, timezone).endOf(interval);
            title = RivalIQ.metrics.new.MetricFormatter.formatDateInterval(startDate, endDate, timezone);
        }

        return [
            startDate.format('MM/DD'),
            value,
            Boolean(intervalMetadata?.[day]?.partialTimePeriod),
            {
                title,
                value
            }
        ];
    });

    const annotationRenderer = createIncompleteAnnotationRenderer({
        data,
        dataKey: 2
    });

    return (
        <>
            <Label>Follower Change By {interval}</Label>
            <Bar
                data={data}
                label={annotationRenderer}
                tooltip={FollowerBarTooltip}
                yAxisAllowDecimals={false}
            />
        </>
    );
}

export default function Audience({instanceId, interval = 'day', pdfExportConfig, appSpecificExportConfig}) {
    const isPPTExport = useIsExportingPPT();

    const landscape = useContext(LandscapeContext);

    const {
        t
    } = useTranslation('apps/insights/components/Audience');

    const timezone = useCurationConfigValue(instanceId, 'timezone');
    const companyId = useCurationConfigValue(instanceId, 'companyId');
    const dateRanges = useComputedCurationConfigValue(instanceId, 'dateRanges');

    const title = t('title');

    useSetExportConfig(instanceId, {
        title,
        supportedExportFormats: _.without(_.values(RivalIQ.enums.exportFormat), RivalIQ.enums.exportFormat.CSV),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig,
        appSpecificExportConfig
    });

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig.timePeriodStr
        }
    });

    const metricRequests = {
        audience: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: 'audience',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                day: 'latestNonNull'
            }]
        },
        audienceDeltaRel: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: {
                field: 'audience',
                transform: 'deltaRelative',
                transformConfig: {
                    comparePeriodStartDate: dateRanges.compare.start,
                    comparePeriodEndDate: dateRanges.compare.end
                }
            },
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                day: 'latestNonNull'
            }]
        },
        audienceByInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId', interval],
            measure: 'audience',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        netFollowers: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: {
                field: 'audience',
                transform: 'deltaAbsolute',
                transformConfig: {
                    comparePeriodStartDate: dateRanges.compare.start,
                    comparePeriodEndDate: dateRanges.compare.end
                }
            },
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                day: 'latestNonNull'
            }]
        },
        netFollowersByInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId', interval],
            measure: 'net_followers',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        }
    };

    return (
        <Panel
            instanceId={instanceId}
            title={title}
            exportSubtitle={appSpecificExportConfig.timePeriodStr}
            description={t('description')}
            showViewMore={false}
        >
            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => {

                        return (
                            <article>
                                <section className='tlw-grid tlw-grid-cols-2 tlw-border-[0] tlw-border-b tlw-border-solid tlw-border-gray-350 tlw-px-7.5 tlw-py-5'>
                                    <FollowersSummary
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                    <NetFollowersSummary
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                </section>
                                <section className='tlw-border-[0] tlw-border-b tlw-border-solid tlw-border-gray-350 tlw-px-7.5 tlw-py-5'>
                                    <FollowersByInterval
                                        instanceId={instanceId}
                                        interval={interval}
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                </section>
                                <section className={isPPTExport ? 'tlw-break-before-page' : 'tlw-px-7.5 tlw-py-5'}>
                                    <FollowerChangeByInterval
                                        interval={interval}
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                </section>
                            </article>
                        );
                    }
                }
            </MetricLoadingView>
        </Panel>
    );
}

Audience.propTypes = {
    appSpecificExportConfig: PropTypes.object
};
